import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import { History } from "history";
import _ from "lodash";
import * as React from "react";
import ReactPaginate from "react-paginate";
import { connect } from "react-redux";
import {
  getPageSize,
  isDbsJwtLoginEnabled,
  isDbsUploadButtonEnabled
} from "src/configState/selector";
import { Entity } from "src/documentUpload/types";
import {
  getCachedSearchOptions,
  getEntityViewState,
  getSearchCount
} from "src/entityView/selector";
import { cacheSearch } from "src/searchCache/actions";
import { SearchOptions } from "src/searchCache/types";
import TextInputWithButton from "src/shared/TextInputWithButton";
import DocumentUploadModal from "../documentUpload/components/DocumentUploadModal";
import { getUser } from "../login/selector";
import { User } from "../login/types";
import { State } from "../state";
import {
  updateActiveTab,
  updateFilter,
  updatePage,
  updateSearch,
  updateStatusGroup
} from "./actions";
import "./DocumentsList.scss";
import DocumentsListColumnHeader from "./DocumentsListColumnHeader";
import DocumentsListRow from "./DocumentsListRow";
import { getDocListPaddingRight, isCompletedState } from "./helpers";
import {
  getActiveTab,
  getBriefSpread,
  getBriefSpreadPage,
  getBriefSpreadQuery
} from "./selector";
import { BriefSpread, BriefSpreadResponse, SPREAD_COLUMNS } from "./types";

interface DocumentsListProps {
  documents: BriefSpreadResponse;
  history: History;
  user: User;
  entity: Entity | null;
  shouldRenderUploadButton: boolean;
  updatePageFunc: typeof updatePage;
  updateFilterFunc: typeof updateFilter;
  updateStatusGroupFunc: typeof updateStatusGroup;
  updateSearchFunc: typeof updateSearch;
  currentPage: number;
  pageSize: number;
  searchTerm?: string;
  performSearch: () => void;
  resetSearch: () => void;
  searchCount: number;
  cachedSearchOptions: SearchOptions;
  cacheSearchFunc: typeof cacheSearch;
  activeTab: number;
  updateActiveTabFunc: typeof updateActiveTab;
  stopPolling: () => void;
  startPolling: () => void;
}

interface DocumentsListState {
  uploadModalOpen: boolean;
}

export class DocumentsList extends React.Component<
  DocumentsListProps,
  DocumentsListState
> {
  public searchInput: any;

  constructor(props: DocumentsListProps) {
    super(props);

    this.state = {
      uploadModalOpen: false
    };
  }

  public handleTabChange = (event: object, newValue: number) => {
    const onlyActive = newValue === 0;
    if (newValue === 2) {
      this.props.updateStatusGroupFunc({ statusGroup: "REJECTED" });
    } else {
      this.props.updateStatusGroupFunc({ statusGroup: "" });
    }
    this.props.updateFilterFunc({ active: onlyActive });
    this.props.updatePageFunc({ selected: 0 });
    this.props.updateActiveTabFunc({ activeTab: newValue });
    this.props.cacheSearchFunc({ activeTab: newValue });
  };

  public filterCompletedState = (document: BriefSpread) => {
    return isCompletedState(document.status);
  };

  public renderAllDocuments = () => {
    return this.props.documents.briefSpreadModels.briefSpreadModels.map(
      document => (
        <DocumentsListRow
          user={this.props.user}
          key={document.spreadId}
          document={document}
          history={this.props.history}
          hasComments={document.hasComments}
        />
      )
    );
  };

  public renderSpreadListView = () => {
    const headerPaddingRight = getDocListPaddingRight();
    const shouldRenderUploadButton = this.props.shouldRenderUploadButton;

    // commenting this to enable updating list upon search
    // if (this.props.searchTerm) {
    //   this.props.stopPolling();
    // }

    return (
      <div className="Documents__container Documents__container--spread-list">
        <div className="Documents__header">
          <div className="Documents__header__upload-container">
            <div className="Documents__header__title">All Spreads</div>

            <div className="Documents__header__search">
              <TextInputWithButton
                autoFocus={!this.state.uploadModalOpen}
                onChange={this.onSearchInput}
                value={this.props.searchTerm || ""}
                className={"searchField"}
                placeholder={"Search"}
                buttonProps={{
                  quiqButtonProps: {
                    type: "submit",
                    color: "blue",
                    onClick: this.onSubmit
                  },
                  buttonText: "Search",
                  hideButton: true
                }}
                onKeyUp={this.keyUp}
                type={"search"}
              />
              <div>
                <p className={"resultsTotal"}>
                  {this.props.searchCount} spreads
                </p>
              </div>
            </div>

            <div className="Documents__header__upload">
              {shouldRenderUploadButton && (
                <DocumentUploadModal
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                />
              )}
            </div>
          </div>

          <Tabs
            className="Documents__tabs"
            value={this.props.activeTab}
            onChange={this.handleTabChange}
            classes={{ indicator: "Documents__tabs__indicator" }}
          >
            <Tab
              className="Documents__tabs__tab"
              label="Active Spreads"
              classes={{
                labelContainer: "Documents__tabs__tab__label",
                selected: "Documents__tabs__tab--selected"
              }}
              disableRipple={true}
            />
            <Tab
              className="Documents__tabs__tab"
              label="Completed Spreads"
              classes={{
                labelContainer: "Documents__tabs__tab__label",
                selected: "Documents__tabs__tab--selected"
              }}
              disableRipple={true}
            />
            <Tab
              className="Documents__tabs__tab"
              label="Rejected Spreads"
              classes={{
                labelContainer: "Documents__tabs__tab__label",
                selected: "Documents__tabs__tab--selected"
              }}
              disableRipple={true}
            />
            <ReactPaginate
              previousLabel={"Back"}
              nextLabel={"Next"}
              breakLabel={"..."}
              breakClassName={"break-me"}
              initialPage={0}
              forcePage={this.props.currentPage}
              pageCount={this.pageCount()}
              marginPagesDisplayed={1}
              pageRangeDisplayed={5}
              onPageChange={this.updatePage}
              containerClassName={"pagination"}
              subContainerClassName={"pages pagination"}
              activeClassName={"active"}
            />
          </Tabs>
        </div>

        <div className="Documents__list">
          <div
            className="Documents__list__header Documents__list__header--spread-list"
            style={headerPaddingRight}
          >
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.ENTITY_NAME}
              className="Documents__list__header__item Documents__column--col-2"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.TIME_PERIOD}
              className="Documents__list__header__item Documents__column--col-3"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.TEMPLATE}
              className="Documents__list__header__item Documents__column--col-4"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.LAST_EDITED_BY}
              className="Documents__list__header__item Documents__column--col-5"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.LAST_EDITED}
              className="Documents__list__header__item Documents__column--col-6"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.UPLOADED_BY}
              className="Documents__list__header__item Documents__column--col-7"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.UPLOAD_TIME}
              className="Documents__list__header__item Documents__column--col-8"
            />

            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.STATUS}
              className="Documents__list__header__item Documents__column--col-10"
            />
          </div>
          <div className="Documents__list-content">
            {this.renderAllDocuments()}
          </div>
        </div>
      </div>
    );
  };

  public renderListInEntityView = () => {
    const headerPaddingRight = getDocListPaddingRight();

    return (
      <div className="Documents__container Documents__container--entity-view">
        <div className="Documents__list EntitiesDocumentList">
          <div
            className="Documents__list__header Documents__list__header--entity-view"
            style={headerPaddingRight}
          >
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.TIME_PERIOD}
              className="Documents__list__header__item Documents__column--col-2"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.TEMPLATE}
              className="Documents__list__header__item Documents__column--col-3"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.LAST_EDITED_BY}
              className="Documents__list__header__item Documents__column--col-4"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.LAST_EDITED}
              className="Documents__list__header__item Documents__column--col-5"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.UPLOADED_BY}
              className="Documents__list__header__item Documents__column--col-6"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.UPLOAD_TIME}
              className="Documents__list__header__item Documents__column--col-7"
            />
            <DocumentsListColumnHeader
              spreadColumn={SPREAD_COLUMNS.STATUS}
              className="Documents__list__header__item Documents__column--col-9"
            />
          </div>
          <div className="Documents__list-content">
            {this.renderAllDocuments()}
          </div>
        </div>
      </div>
    );
  };

  public render() {
    const isNestedInEntityView = this.props.entity !== null;

    return isNestedInEntityView
      ? this.renderListInEntityView()
      : this.renderSpreadListView();
  }

  private pageCount = () => {
    return this.props.documents.briefSpreadCount / this.props.pageSize;
  };

  private keyUp = (e: any) => {
    const query = e.target.value;

    if (e.key === "Enter" && query.length > 1) {
      this.props.updatePageFunc({ selected: 0 });
      this.props.performSearch();
    }
  };

  private onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.props.stopPolling();
    this.props.cacheSearchFunc({ query });
    this.props.updateSearchFunc({ query });

    if (!e.target.value) {
      this.props.startPolling();
      this.props.updatePageFunc({ selected: 0 });
      this.props.resetSearch();
    }
  };

  private onSubmit = () => {
    this.props.performSearch();
  };

  private updatePage = (params: any) => {
    this.props.updatePageFunc(params);
  };

  private onOpen = () => {
    this.setState({ uploadModalOpen: true });
  };

  private onClose = () => {
    this.setState({ uploadModalOpen: false });
  };
}

const mapStateToProps = (state: State) => ({
  documents: getBriefSpread(state),
  user: getUser(state),
  entity: getEntityViewState(state),
  currentPage: getBriefSpreadPage(state),
  pageSize: getPageSize(state),
  searchTerm: getBriefSpreadQuery(state),
  searchCount: getSearchCount(state),
  cachedSearchOptions: getCachedSearchOptions(state),
  activeTab: getActiveTab(state),
  shouldRenderUploadButton:
    !isDbsJwtLoginEnabled(state) ||
    (isDbsJwtLoginEnabled(state) && isDbsUploadButtonEnabled(state))
});

export default connect(
  mapStateToProps,
  {
    updatePageFunc: updatePage,
    updateFilterFunc: updateFilter,
    updateStatusGroupFunc: updateStatusGroup,
    updateSearchFunc: updateSearch,
    cacheSearchFunc: cacheSearch,
    updateActiveTabFunc: updateActiveTab
  }
)(DocumentsList);



// WEBPACK FOOTER //
// ./src/documentList/DocumentsList.tsx